import React, { useState, useMemo } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useEntries } from "../hooks/useEntries";
import { useHuchas } from "../hooks/useHuchas";
import { t } from "../i18n";
import { formatAmount } from "../config/currencies";
import { getThemePalette } from "../config/themePalette";
import {
  PiggyBank, Plane, Home, Car, Laptop, GraduationCap, Gem, Gift, Umbrella,
  Stethoscope, ShieldCheck, Target, Plus, Minus, Trash2, ChevronDown, ChevronRight,
} from "lucide-react";

// Antes cada hucha guardaba un emoji tal cual (ej. "🐷") en h.icon. Ahora se
// guarda la clave de un icono vectorial (ej. "piggy") en su lugar. Las huchas
// que ya existían de antes conservan su emoji guardado — IconFor() lo detecta
// y lo sigue mostrando tal cual, para no romper nada ya creado.
const ICONS = [
  { key: "piggy",  Icon: PiggyBank },
  { key: "plane",  Icon: Plane },
  { key: "home",   Icon: Home },
  { key: "car",    Icon: Car },
  { key: "laptop", Icon: Laptop },
  { key: "grad",   Icon: GraduationCap },
  { key: "gem",    Icon: Gem },
  { key: "gift",   Icon: Gift },
  { key: "beach",  Icon: Umbrella },
  { key: "health", Icon: Stethoscope },
  { key: "shield", Icon: ShieldCheck },
  { key: "target", Icon: Target },
];
function IconFor({ iconKey, size = 22 }) {
  const found = ICONS.find(i => i.key === iconKey);
  if (found) { const C = found.Icon; return <C size={size} />; }
  // Hucha antigua con emoji guardado directamente — se muestra igual que antes.
  return <span style={{fontSize:size}}>{iconKey}</span>;
}

export default function Huchas() {
  const { lang, currency, theme } = useAuth();
  const { addEntry } = useEntries();
  const { huchas, loading, addHucha, deleteHucha, aportar, retirar, getProgress } = useHuchas();
  const T = (key) => t(lang, key);
  const fmt = n => formatAmount(n, currency);

  // Paleta de colores cálidos/pastel filtrada según el tema activo, para
  // elegir el color de fondo del icono de cada hucha.
  const COLORS = useMemo(() => getThemePalette(theme), [theme]);

  // Crear hucha nueva
  const [showForm, setShowForm] = useState(false);
  const [newName, setNewName] = useState("");
  const [newGoal, setNewGoal] = useState("");
  const [newIcon, setNewIcon] = useState("piggy");
  const [newColor, setNewColor] = useState(COLORS[0]);

  // Aportar/retirar
  const [actionId, setActionId] = useState(null); // id de la hucha con el formulario abierto
  const [actionType, setActionType] = useState(null); // "aportar" | "retirar"
  const [actionAmount, setActionAmount] = useState("");
  const [actionError, setActionError] = useState("");
  const [actionSaving, setActionSaving] = useState(false);

  // Detalle expandido (historial de movimientos)
  const [expanded, setExpanded] = useState(null);

  async function handleCreate() {
    if (!newName.trim()) return;
    await addHucha({ name: newName.trim(), goal: newGoal, icon: newIcon, color: newColor });
    setNewName(""); setNewGoal(""); setNewIcon("piggy"); setNewColor("#7c3aed");
    setShowForm(false);
  }

  function startAction(hucha, type, ev) {
    if (ev) ev.stopPropagation();
    setActionId(hucha.id);
    setActionType(type);
    setActionAmount("");
    setActionError("");
  }

  async function confirmAction(hucha) {
    setActionError("");
    const monto = parseFloat(String(actionAmount).replace(",", "."));
    if (!monto || monto <= 0) {
      setActionError(lang==="en" ? "Enter a valid amount" : "Introduce un importe válido");
      return;
    }
    if (actionType === "retirar" && monto > (hucha.saldo || 0)) {
      setActionError(lang==="en"
        ? `Can't withdraw more than the current balance of ${fmt(hucha.saldo||0)}`
        : `No puedes retirar más del saldo actual de ${fmt(hucha.saldo||0)}`);
      return;
    }
    setActionSaving(true);
    if (actionType === "aportar") await aportar(hucha, monto, addEntry);
    else await retirar(hucha, monto, addEntry);
    setActionSaving(false);
    setActionId(null);
    setActionType(null);
    setActionAmount("");
  }

  if (loading) return <div className="page"><p style={{color:"var(--gray-400)"}}>Cargando...</p></div>;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h2 className="page-title" style={{display:"flex",alignItems:"center",gap:8}}>
            <PiggyBank size={22}/> {lang==="en"?"Savings jars":"Huchas"}
          </h2>
          <p className="page-sub">
            {lang==="en"
              ? "Save towards a goal. Balances build up over time and never reset."
              : "Ahorra para un objetivo. El saldo se acumula con el tiempo y nunca se reinicia."}
          </p>
        </div>
        <button className="btn btn-primary" onClick={()=>setShowForm(!showForm)} style={{display:"flex",alignItems:"center",gap:6}}>
          <Plus size={16}/> {lang==="en"?"New jar":"Nueva hucha"}
        </button>
      </div>

      {/* Formulario de creación */}
      {showForm && (
        <div className="card" style={{marginBottom:20}}>
          <h3 style={{fontSize:15,fontWeight:600,marginBottom:14}}>
            {lang==="en"?"Create a new savings jar":"Crear una nueva hucha"}
          </h3>
          <div style={{display:"flex",gap:10,flexWrap:"wrap",marginBottom:12}}>
            <div style={{flex:2,minWidth:160}}>
              <label style={{fontSize:12,color:"var(--gray-500)",display:"block",marginBottom:4}}>
                {lang==="en"?"Name":"Nombre"}
              </label>
              <input className="form-input" value={newName} onChange={e=>setNewName(e.target.value)}
                     placeholder={lang==="en"?"e.g. Vacation":"Ej: Vacaciones"} autoFocus/>
            </div>
            <div style={{flex:1,minWidth:120}}>
              <label style={{fontSize:12,color:"var(--gray-500)",display:"block",marginBottom:4}}>
                {lang==="en"?"Goal (optional)":"Meta (opcional)"}
              </label>
              <input className="form-input" type="number" step="0.01" min="0" value={newGoal}
                     onChange={e=>setNewGoal(e.target.value)} placeholder="0.00"/>
            </div>
          </div>
          <div style={{marginBottom:12}}>
            <label style={{fontSize:12,color:"var(--gray-500)",display:"block",marginBottom:4}}>
              {lang==="en"?"Icon":"Icono"}
            </label>
            <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
              {ICONS.map(({key,Icon})=>(
                <button key={key} type="button" onClick={()=>setNewIcon(key)}
                  style={{width:34,height:34,borderRadius:8,cursor:"pointer",display:"flex",
                          alignItems:"center",justifyContent:"center",
                          border:`2px solid ${newIcon===key?"var(--blue)":"var(--border)"}`,
                          background:newIcon===key?"var(--blue-l)":"var(--card)",
                          color:newIcon===key?"var(--blue)":"var(--gray-600)"}}>
                  <Icon size={17}/>
                </button>
              ))}
            </div>
          </div>
          <div style={{marginBottom:16}}>
            <label style={{fontSize:12,color:"var(--gray-500)",display:"block",marginBottom:4}}>
              {lang==="en"?"Color":"Color"}
            </label>
            <div style={{display:"flex",gap:6}}>
              {COLORS.map(col=>(
                <button key={col} type="button" onClick={()=>setNewColor(col)}
                  style={{width:24,height:24,borderRadius:"50%",cursor:"pointer",background:col,border:"none",
                          outline:newColor===col?"2px solid var(--gray-600)":"none",outlineOffset:2}}/>
              ))}
            </div>
          </div>
          <div style={{display:"flex",gap:8}}>
            <button className="btn btn-primary" onClick={handleCreate} disabled={!newName.trim()}>
              {lang==="en"?"Create":"Crear"}
            </button>
            <button className="btn btn-outline" onClick={()=>setShowForm(false)}>
              {lang==="en"?"Cancel":"Cancelar"}
            </button>
          </div>
        </div>
      )}

      {/* Lista de huchas */}
      {huchas.length === 0 ? (
        <div className="empty">
          <div className="empty-icon"><PiggyBank size={32}/></div>
          <h3>{lang==="en"?"No savings jars yet":"Todavía no tienes huchas"}</h3>
          <p>{lang==="en"?"Create one above to start saving towards a goal":"Crea una arriba para empezar a ahorrar para un objetivo"}</p>
        </div>
      ) : (
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(min(100%,320px),1fr))",gap:16}}>
          {huchas.map(h => {
            const progress = getProgress(h);
            const isOpen = expanded === h.id;
            const isActing = actionId === h.id;
            return (
              <div key={h.id} className="card" style={{padding:"18px 20px"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12}}>
                  <div style={{display:"flex",alignItems:"center",gap:10}}>
                    <span style={{fontSize:28,width:44,height:44,borderRadius:12,display:"flex",
                                  alignItems:"center",justifyContent:"center",background:h.color+"22",color:h.color}}>
                      <IconFor iconKey={h.icon} size={22}/>
                    </span>
                    <div>
                      <p style={{fontSize:15,fontWeight:700,color:"var(--gray-800)"}}>{h.name}</p>
                      <p style={{fontSize:13,color:"var(--gray-500)"}}>
                        {h.goal
                          ? `${fmt(h.saldo||0)} / ${fmt(h.goal)}`
                          : fmt(h.saldo||0)}
                      </p>
                    </div>
                  </div>
                  <button onClick={()=>{ if(window.confirm(lang==="en"?"Delete this jar?":"¿Eliminar esta hucha?")) deleteHucha(h.id); }}
                    style={{background:"none",border:"none",color:"var(--gray-400)",cursor:"pointer",display:"flex"}}>
                    <Trash2 size={16}/>
                  </button>
                </div>

                {progress !== null && (
                  <div style={{marginBottom:14}}>
                    <div className="limit-bar" style={{height:10}}>
                      <div style={{height:"100%",borderRadius:6,width:`${progress}%`,
                                   background:progress>=100?"var(--green)":h.color,transition:"width .3s"}}/>
                    </div>
                    <p style={{fontSize:12,color:"var(--gray-400)",marginTop:4,textAlign:"right"}}>{progress}%</p>
                  </div>
                )}

                {/* Formulario de aportar/retirar */}
                {isActing ? (
                  <div style={{background:"var(--gray-50)",borderRadius:8,padding:"12px 14px",marginBottom:10}}>
                    <p style={{fontSize:13,fontWeight:600,marginBottom:8,display:"flex",alignItems:"center",gap:6,
                               color:actionType==="aportar"?"var(--green)":"var(--blue)"}}>
                      {actionType==="aportar"
                        ? <><Plus size={14}/> {lang==="en"?"Add money":"Aportar dinero"}</>
                        : <><Minus size={14}/> {lang==="en"?"Withdraw money":"Retirar dinero"}</>}
                    </p>
                    {actionError && <div className="alert alert-error" style={{marginBottom:8,padding:"6px 10px",fontSize:12}}>{actionError}</div>}
                    <input className="form-input" type="number" step="0.01" min="0.01" value={actionAmount}
                           onChange={e=>setActionAmount(e.target.value)} placeholder="0.00" autoFocus
                           style={{marginBottom:10}}/>
                    <div style={{display:"flex",gap:8}}>
                      <button className="btn btn-primary btn-sm" onClick={()=>confirmAction(h)} disabled={actionSaving}>
                        {actionSaving ? "..." : (lang==="en"?"Confirm":"Confirmar")}
                      </button>
                      <button className="btn btn-outline btn-sm" onClick={()=>{setActionId(null);setActionError("");}}>
                        {lang==="en"?"Cancel":"Cancelar"}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div style={{display:"flex",gap:8,marginBottom:8}}>
                    <button className="btn btn-primary btn-sm" onClick={(ev)=>startAction(h,"aportar",ev)} style={{display:"flex",alignItems:"center",gap:5}}>
                      <Plus size={14}/> {lang==="en"?"Add":"Aportar"}
                    </button>
                    <button className="btn btn-outline btn-sm" onClick={(ev)=>startAction(h,"retirar",ev)}
                            disabled={(h.saldo||0) <= 0} style={{display:"flex",alignItems:"center",gap:5}}>
                      <Minus size={14}/> {lang==="en"?"Withdraw":"Retirar"}
                    </button>
                  </div>
                )}

                {/* Historial de movimientos */}
                {(h.movimientos||[]).length > 0 && (
                  <button onClick={()=>setExpanded(isOpen?null:h.id)}
                    style={{background:"none",border:"none",color:"var(--gray-400)",cursor:"pointer",
                            fontSize:12,padding:0,textAlign:"left",display:"flex",alignItems:"center",gap:4}}>
                    {isOpen ? <ChevronDown size={13}/> : <ChevronRight size={13}/>}
                    {isOpen
                      ? (lang==="en"?"Hide history":"Ocultar historial")
                      : (lang==="en"?`View history (${h.movimientos.length})`:`Ver historial (${h.movimientos.length})`)}
                  </button>
                )}
                {isOpen && (
                  <div style={{marginTop:8,display:"flex",flexDirection:"column",gap:4,maxHeight:160,overflowY:"auto"}}>
                    {[...(h.movimientos||[])].reverse().map((m,i)=>(
                      <div key={i} style={{display:"flex",justifyContent:"space-between",fontSize:12,
                                            background:"var(--gray-50)",borderRadius:6,padding:"6px 10px"}}>
                        <span style={{color:"var(--gray-500)"}}>{m.date}</span>
                        <span style={{fontWeight:600,color:m.type==="aporte"?"var(--green)":"var(--red)"}}>
                          {m.type==="aporte"?"+":"-"}{fmt(m.amount)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
